/* Ricordo Clothing only: seasonal photographs, user-controlled rotation. */
if (!customElements.get('ricordo-wardrobe-carousel')) {
  customElements.define('ricordo-wardrobe-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slides = [...this.querySelectorAll('[data-wardrobe-slide]')];
      this.dots = [...this.querySelectorAll('[data-wardrobe-dot]')];
      this.controls = this.querySelector('[data-wardrobe-controls]');
      this.toggle = this.querySelector('[data-wardrobe-toggle]');
      this.status = this.querySelector('[data-wardrobe-status]');
      if (!this.slides.length || !this.controls || !this.toggle) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = this.motion.matches;
      this.hovered = false;
      this.inView = false;
      this.timer = null;
      this.controls.hidden = this.slides.length < 2;
      this.classList.add('is-enhanced');
      this.querySelector('[data-wardrobe-previous]')?.addEventListener('click', () => this.go(this.index - 1, true), options);
      this.querySelector('[data-wardrobe-next]')?.addEventListener('click', () => this.go(this.index + 1, true), options);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index, true), options));
      this.toggle.addEventListener('pointerdown', () => { this.togglePointerState = this.paused; }, options);
      this.toggle.addEventListener('pointercancel', () => { this.togglePointerState = undefined; }, options);
      this.toggle.addEventListener('click', () => {
        this.paused = !(this.togglePointerState ?? this.paused);
        this.togglePointerState = undefined;
        this.updateControls();
        this.schedule();
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch') { this.hovered = true; this.schedule(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', event => {
        if (!this.contains(event.relatedTarget)) {
          this.paused = true;
          this.updateControls();
          this.schedule();
        }
      }, options);
      this.addEventListener('keydown', event => {
        const next = event.key === 'ArrowRight' ? this.index + 1 : event.key === 'ArrowLeft' ? this.index - 1 : event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : null;
        if (next === null) return;
        event.preventDefault();
        const focusedSlide = event.target.closest('[data-wardrobe-slide]');
        this.go(next, true);
        if (focusedSlide) this.dots[this.index]?.focus();
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.updateControls();
        this.schedule();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.go(0);
    }
    go(index, manual = false) {
      if (manual) this.paused = true;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        slide.querySelectorAll('a[href]').forEach(link => { link.tabIndex = active ? 0 : -1; });
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.updateControls();
      if (this.status) this.status.textContent = this.slides[this.index].dataset.announcement || '';
      this.schedule();
    }
    updateControls() {
      this.toggle.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.toggle.querySelector('[data-pause-icon]').hidden = this.paused;
      this.toggle.querySelector('[data-play-icon]').hidden = !this.paused;
      if (this.status) this.status.setAttribute('aria-live', this.paused ? 'polite' : 'off');
      this.dataset.rotation = this.paused ? 'paused' : 'playing';
    }
    schedule() {
      clearTimeout(this.timer);
      this.timer = null;
      if (!this.paused && !this.hovered && this.inView && !document.hidden && this.slides.length > 1) {
        this.timer = setTimeout(() => this.go(this.index + 1), 7000);
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.observer?.disconnect();
      this.initialized = false;
    }
  });
}
